'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  ChevronRight,
  Play,
  MapPin,
  Truck,
  Camera,
  Brain,
  Shield,
  FileBarChart,
  Cpu,
  Bell,
  ArrowRight,
  Sparkles,
  Zap,
  Users,
  Paintbrush,
  Smartphone,
  Heart,
  Tractor,
  Package,
  Globe,
  ShieldCheck,
  Satellite,
  CircuitBoard,
  BarChart3,
  Lock,
  Layers,
} from 'lucide-react';
import { HOME_SECTION_IMAGES, PAGE_HERO_IMAGES } from '@/lib/marketing-images';
import { fetchRecentPublicBlogArticles, type PublicRecentArticle } from '@/lib/api';
import { resolvePublicBlogScope } from '@/lib/public-blog-scope';

/* ── Industries Data ───────────────────────────────────────── */
const industries = [
  {
    icon: ShieldCheck,
    title: 'Insurance & UBI',
    desc: 'Usage-Based Insurance scoring, risk profiling, crash reconstruction, and claims automation for insurers and underwriters.',
    href: '/industries/insurance-ubi',
    color: 'from-neon-purple to-neon-blue',
    glow: 'rgba(168, 85, 247, 0.15)',
  },
  {
    icon: Heart,
    title: 'Ambulance & Emergency',
    desc: 'Real-time dispatch optimization, nearest-unit routing, response-time SLA tracking, and patient transport monitoring.',
    href: '/industries/ambulance-emergency',
    color: 'from-neon-magenta to-neon-purple',
    glow: 'rgba(244, 63, 94, 0.15)',
  },
  {
    icon: Tractor,
    title: 'Livestock & Farming',
    desc: 'GPS collar tracking for cattle, geofence alerts for grazing boundaries, theft prevention, and herd health monitoring.',
    href: '/industries/livestock-farming',
    color: 'from-neon-teal to-neon-cyan',
    glow: 'rgba(45, 212, 191, 0.15)',
  },
  {
    icon: Truck,
    title: 'Fleet Management',
    desc: 'End-to-end fleet visibility, driver behavior scoring, fuel analytics, maintenance scheduling, and compliance automation.',
    href: '/industries/fleet-management',
    color: 'from-neon-cyan to-neon-blue',
    glow: 'rgba(0, 212, 255, 0.15)',
  },
  {
    icon: Package,
    title: 'Logistics & Supply Chain',
    desc: 'Proof-of-delivery, cold-chain monitoring, warehouse-to-door tracking, ETA prediction, and route optimization.',
    href: '/industries/logistics',
    color: 'from-neon-amber to-neon-magenta',
    glow: 'rgba(245, 158, 11, 0.15)',
  },
  {
    icon: Paintbrush,
    title: 'White-Label Platform',
    desc: 'Launch your own branded telematics platform with custom domains, logos, CMS pages, and full API access.',
    href: '/white-label',
    color: 'from-neon-cyan to-neon-purple',
    glow: 'rgba(0, 212, 255, 0.15)',
  },
];

/* ── Platform Features ─────────────────────────────────────── */
const features = [
  { icon: MapPin, title: 'Live Tracking', desc: 'Real-time GPS with sub-second SSE streaming and smart clustering.' },
  { icon: Camera, title: 'Camera Intelligence', desc: 'Quad-cam HLS streaming with ADAS/DMS AI safety automation.' },
  { icon: Brain, title: 'AI Copilot', desc: 'Anomaly detection, predictive maintenance, route optimization.' },
  { icon: Shield, title: 'Driver Safety', desc: 'Safety scorecards, harsh events, fatigue detection, UBI scoring.' },
  { icon: FileBarChart, title: 'Reports & Analytics', desc: '100+ reports across 9 categories with scheduled delivery.' },
  { icon: Bell, title: 'Smart Alerts', desc: '100+ configurable triggers with multi-channel delivery.' },
  { icon: Cpu, title: 'Device & IoT Hub', desc: 'Onboarding, remote config, OTA updates, SMS provisioning.' },
  { icon: Smartphone, title: 'Mobile App', desc: 'Native iOS & Android with live maps, cameras, and billing.' },
  { icon: Lock, title: 'Enterprise Security', desc: 'RBAC, MFA, AES-256, SOC2 controls, audit logging.' },
];

/* ── Gadgets Preview ───────────────────────────────────────── */
const gadgets = [
  { name: 'GT-X1 GPS Tracker', category: 'Vehicle Tracking', desc: 'Hardwired OBD-II tracker with 4G LTE, accelerometer, and tamper alerts.', specs: ['4G LTE', 'IP67', '10-year battery'] },
  { name: 'GT-CAM Pro', category: 'Dash Camera', desc: 'Dual-facing AI dashcam with ADAS, DMS, and cloud recording.', specs: ['1080p', 'Night Vision', 'AI Edge'] },
  { name: 'GT-Collar V2', category: 'Livestock Tracker', desc: 'Solar-powered GPS collar for cattle and high-value livestock.', specs: ['Solar + Battery', 'IP68', 'LoRa + 4G'] },
  { name: 'GT-Temp Sensor', category: 'Cold Chain', desc: 'Bluetooth temperature sensor for cold-chain and pharma logistics.', specs: ['-40 to 80C', 'BLE 5.0', 'FDA compliant'] },
  { name: 'GT-Asset Tag', category: 'Asset Tracking', desc: 'Compact magnetic tracker for trailers, containers, and high-value assets.', specs: ['5-year battery', 'LTE-M', 'Magnetic mount'] },
  { name: 'GT-OBD Pro', category: 'Diagnostics', desc: 'OBD-II diagnostic reader with real-time engine data and fault codes.', specs: ['CAN Bus', 'J1939', 'Plug & Play'] },
];

/* ── Stats ─────────────────────────────────────────────────── */
const stats = [
  { value: '50K+', label: 'Active Devices' },
  { value: '300+', label: 'Organizations' },
  { value: '99.9%', label: 'Platform Uptime' },
  { value: '42', label: 'Countries' },
];

/* ── White-label benefits ──────────────────────────────────── */
const whitelabelBenefits = [
  { icon: Paintbrush, title: 'Your Brand, Your Platform', desc: 'Custom logos, colors, domains, and full CMS for your landing pages.' },
  { icon: Layers, title: 'Multi-Tenant Architecture', desc: 'Isolated data, separate billing, and role-based access for each client.' },
  { icon: Globe, title: 'Global Infrastructure', desc: 'Edge nodes across 6 continents with sub-100ms latency worldwide.' },
  { icon: BarChart3, title: 'Revenue Analytics', desc: 'Track MRR, churn, device activations, and client health in real-time.' },
];

function to35Words(value: string): string {
  const words = value
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  if (words.length <= 35) return words.join(' ');
  return `${words.slice(0, 35).join(' ')}...`;
}

const RECENT_BLOG_CACHE_TTL_MS = 5 * 60 * 1000;
const BLOG_SCOPE = resolvePublicBlogScope();
const getRecentBlogCacheKey = () =>
  `glonextrack:marketing:recentBlogs:v1:${BLOG_SCOPE.cacheKey}`;

type RecentBlogsCache = {
  timestamp: number;
  articles: PublicRecentArticle[];
};

function readRecentBlogsCache(): PublicRecentArticle[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getRecentBlogCacheKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RecentBlogsCache;
    if (!parsed || typeof parsed.timestamp !== 'number' || !Array.isArray(parsed.articles)) {
      return null;
    }
    if (Date.now() - parsed.timestamp > RECENT_BLOG_CACHE_TTL_MS) return null;
    return parsed.articles.slice(0, 8);
  } catch {
    return null;
  }
}

function writeRecentBlogsCache(articles: PublicRecentArticle[]) {
  if (typeof window === 'undefined') return;
  try {
    const payload: RecentBlogsCache = {
      timestamp: Date.now(),
      articles: articles.slice(0, 8),
    };
    window.localStorage.setItem(getRecentBlogCacheKey(), JSON.stringify(payload));
  } catch {
    // Ignore quota/storage errors; landing page should still render.
  }
}

export default function LandingPage() {
  const [recentBlogs, setRecentBlogs] = useState<PublicRecentArticle[]>([]);
  const [recentBlogsLoading, setRecentBlogsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const cachedBlogs = readRecentBlogsCache();
    const hasCachedBlogs = Boolean(cachedBlogs?.length);

    if (hasCachedBlogs && cachedBlogs) {
      setRecentBlogs(cachedBlogs);
      setRecentBlogsLoading(false);
    }

    void fetchRecentPublicBlogArticles({
      limit: 8,
      organizationId: BLOG_SCOPE.organizationId,
      organizationSlug: BLOG_SCOPE.organizationSlug,
    })
      .then((articles) => {
        if (!active) return;
        const next = articles.slice(0, 8);
        setRecentBlogs(next);
        writeRecentBlogsCache(next);
      })
      .catch(() => {
        if (!active) return;
        if (!hasCachedBlogs) setRecentBlogs([]);
      })
      .finally(() => {
        if (!active) return;
        if (!hasCachedBlogs) setRecentBlogsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      {/* ══════════════════════════════════════════════════════
          HERO
          ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden gradient-hero-vivid noise-overlay grid-bg">
        {/* Vivid animated orbs */}
        <motion.div
          className="absolute top-1/4 -left-20 h-[700px] w-[700px] rounded-full opacity-30 blur-[120px] gradient-orb-cyan"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -top-20 -right-20 h-[600px] w-[600px] rounded-full opacity-25 blur-[100px] gradient-orb-purple"
          animate={{ x: [0, -25, 0], y: [0, 30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 left-1/3 h-[500px] w-[500px] rounded-full opacity-20 blur-[100px] gradient-orb-magenta"
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 right-1/4 h-[300px] w-[300px] rounded-full opacity-15 blur-[80px] gradient-orb-amber"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-20 pt-24 lg:px-8 lg:pb-32 lg:pt-36">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="space-y-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan">
                  <Sparkles className="h-3 w-3" />
                  Africa-born &middot; Global Scale &middot; Neon-Powered
                </span>
              </motion.div>

              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
                Telematics{' '}
                <span className="text-gradient">Intelligence,</span>
                <br />
                Reimagined.
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="max-w-lg text-lg leading-relaxed text-slate-300">
                Track. Protect. Optimize. The most advanced telematics platform for fleets, insurers,
                ambulance services, farmers, and enterprises. Real-time GPS, camera AI, driver safety,
                UBI scoring, and full white-label capability &mdash; unified in one platform.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-col gap-3 sm:flex-row">
                <Link href="/signup">
                  <button className="btn-neon rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2 shadow-lg">
                    Start Free Trial
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </Link>
                <Link href="#industries">
                  <button className="btn-neon-outline rounded-full px-8 py-3.5 text-sm font-semibold flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    Explore Industries
                  </button>
                </Link>
              </motion.div>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-sm text-slate-300">
                14-day free trial &middot; No credit card required &middot; Setup in 5 minutes
              </motion.p>
            </div>

            {/* Futuristic Orb Visualization */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative flex flex-col items-center justify-center gap-8">
              <div className="w-full max-w-2xl glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.home.src}
                  alt={PAGE_HERO_IMAGES.home.alt}
                  width={1600}
                  height={900}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
              <div className="relative h-[400px] w-[400px] lg:h-[500px] lg:w-[500px]">
                {/* Outer ring */}
                <div className="absolute inset-0 rounded-full border border-neon-cyan/10 animate-[spin_40s_linear_infinite]" />
                <div className="absolute inset-4 rounded-full border border-neon-purple/10 animate-[spin_30s_linear_infinite_reverse]" />
                <div className="absolute inset-10 rounded-full border border-neon-cyan/5" />

                {/* Core orb */}
                <div className="absolute inset-16 rounded-full" style={{
                  background: 'radial-gradient(circle at 30% 30%, rgba(0,212,255,0.2), rgba(168,85,247,0.1) 50%, transparent 70%)',
                  boxShadow: '0 0 60px rgba(0,212,255,0.1), 0 0 120px rgba(168,85,247,0.05), inset 0 0 60px rgba(0,212,255,0.05)',
                }} />

                {/* Orbiting dots */}
                {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                  <div key={deg} className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2" style={{ animation: `orbit ${15 + i * 3}s linear infinite`, animationDelay: `${i * -2}s` }}>
                    <div className="h-2 w-2 rounded-full bg-neon-cyan" style={{ boxShadow: '0 0 10px rgba(0,212,255,0.5)' }} />
                  </div>
                ))}

                {/* Floating icons */}
                {[
                  { Icon: Truck, x: '10%', y: '20%', delay: 0 },
                  { Icon: Shield, x: '80%', y: '15%', delay: 1 },
                  { Icon: Satellite, x: '85%', y: '70%', delay: 2 },
                  { Icon: Heart, x: '15%', y: '75%', delay: 0.5 },
                  { Icon: Tractor, x: '50%', y: '5%', delay: 1.5 },
                ].map(({ Icon, x, y, delay }) => (
                  <motion.div
                    key={`${x}-${y}`}
                    className="absolute glass-card rounded-xl p-2.5"
                    style={{ left: x, top: y }}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, delay }}
                  >
                    <Icon className="h-4 w-4 text-neon-cyan/70" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((stat, i) => {
              const statGlows = [
                { border: 'rgba(0,212,255,0.2)', shadow: '0 0 20px rgba(0,212,255,0.1), 0 0 40px rgba(0,212,255,0.04)' },
                { border: 'rgba(168,85,247,0.2)', shadow: '0 0 20px rgba(168,85,247,0.1), 0 0 40px rgba(168,85,247,0.04)' },
                { border: 'rgba(244,63,94,0.2)', shadow: '0 0 20px rgba(244,63,94,0.1), 0 0 40px rgba(244,63,94,0.04)' },
                { border: 'rgba(45,212,191,0.2)', shadow: '0 0 20px rgba(45,212,191,0.1), 0 0 40px rgba(45,212,191,0.04)' },
              ];
              const glow = statGlows[i];
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="glass-card rounded-2xl p-6 text-center"
                  style={{ borderColor: glow.border, boxShadow: glow.shadow }}
                >
                  <div className="text-3xl font-bold text-shimmer">{stat.value}</div>
                  <div className="mt-1 text-sm text-slate-300">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0f0e2e] to-transparent" />
      </section>

      {/* ══════════════════════════════════════════════════════
          TRUSTED BY LEADERS – Colorful Marquee
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-12 overflow-hidden border-y border-white/10">
        <div className="absolute inset-0 gradient-rainbow opacity-60" />
        <div className="relative z-10">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-300 mb-6">
            Trusted by Industry Leaders Across <span className="text-neon-cyan">42 Countries</span>
          </p>
          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-[#0f0e2e] to-transparent" />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-[#0f0e2e] to-transparent" />
            <div className="flex animate-marquee">
              {[...Array(2)].map((_, setIdx) => (
                <div key={setIdx} className="flex shrink-0 items-center gap-12 px-6">
                  {[
                    { name: 'FleetPro SA', color: '#00d4ff' },
                    { name: 'SafeDrive Insurance', color: '#a855f7' },
                    { name: 'MediRush EMS', color: '#f43f5e' },
                    { name: 'AgriTrack Global', color: '#2dd4bf' },
                    { name: 'ColdChain Express', color: '#f59e0b' },
                    { name: 'UrbanLogix', color: '#3b82f6' },
                    { name: 'PanAfrica Motors', color: '#00d4ff' },
                    { name: 'Savanna Livestock', color: '#a855f7' },
                    { name: 'TransCape Freight', color: '#f43f5e' },
                    { name: 'SecureFleet UK', color: '#2dd4bf' },
                  ].map((partner) => (
                    <div key={`${setIdx}-${partner.name}`} className="flex items-center gap-2 whitespace-nowrap">
                      <div className="h-2 w-2 rounded-full" style={{ background: partner.color, boxShadow: `0 0 8px ${partner.color}40` }} />
                      <span className="text-sm font-semibold tracking-wide" style={{ color: `${partner.color}99` }}>
                        {partner.name}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="mx-auto mt-8 max-w-4xl px-6">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={HOME_SECTION_IMAGES.trustedBy.src}
                alt={HOME_SECTION_IMAGES.trustedBy.alt}
                width={1600}
                height={900}
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1280px) 100vw, 1200px"
                className="h-auto w-full rounded-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          INDUSTRIES WE SERVE
          ══════════════════════════════════════════════════════ */}
      <section id="industries" className="defer-render relative py-24 lg:py-32 gradient-hero-vivid">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan mb-4">
              Industries We Serve
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Purpose-Built for <span className="text-gradient">Every Industry</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              From insurance telematics to livestock farming, our platform adapts to your industry&apos;s unique challenges with specialized tools and workflows.
            </p>
          </div>

          <div className="mb-10">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={HOME_SECTION_IMAGES.industries.src}
                alt={HOME_SECTION_IMAGES.industries.alt}
                width={1600}
                height={900}
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1280px) 100vw, 1200px"
                className="h-auto w-full rounded-2xl object-cover"
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((ind, i) => (
              <motion.div
                key={ind.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
              >
                <Link href={ind.href} className="group block h-full">
                  <div className="glass-card-vivid h-full rounded-2xl p-8" style={{ boxShadow: `0 0 0 0 transparent` }}>
                    <div className={`mb-5 inline-flex rounded-xl bg-gradient-to-br ${ind.color} p-3.5 text-white shadow-lg`} style={{ boxShadow: `0 8px 30px ${ind.glow}, 0 0 15px ${ind.glow}` }}>
                      <ind.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-white">{ind.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-300">{ind.desc}</p>
                    <div className="mt-5 flex items-center gap-1 text-sm font-medium text-neon-cyan opacity-0 transition-opacity group-hover:opacity-100">
                      Explore solutions <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          PLATFORM FEATURES
          ══════════════════════════════════════════════════════ */}
      <section className="defer-render relative py-24 lg:py-32 gradient-hero-vivid">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-4">
              Platform Capabilities
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Everything You Need to <span className="text-gradient">Dominate Fleet Ops</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              A single pane of glass for tracking, safety, AI, camera intelligence, analytics, and financial automation.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feat, i) => {
              const featureColors = [
                '#00d4ff', '#f43f5e', '#a855f7',
                '#2dd4bf', '#f59e0b', '#3b82f6',
                '#00d4ff', '#a855f7', '#f43f5e',
              ];
              const borderColor = featureColors[i % featureColors.length];
              return (
                <motion.div
                  key={feat.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="glass-card rounded-2xl p-6 group overflow-hidden"
                  style={{ borderLeft: `2px solid ${borderColor}40` }}
                >
                  <div className="mb-4 inline-flex rounded-xl p-3 transition-colors" style={{ background: `${borderColor}10`, color: borderColor }}>
                    <feat.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-1.5 text-base font-semibold text-white">{feat.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-300">{feat.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HARDWARE & GADGETS PREVIEW
          ══════════════════════════════════════════════════════ */}
      <section className="defer-render relative py-24 lg:py-32 gradient-hero-vivid">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-teal mb-4">
              <CircuitBoard className="h-3 w-3" />
              Hardware Portfolio
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Enterprise-Grade <span className="text-gradient-cool">Devices & Sensors</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              We supply, configure, and manage the full hardware stack. Every device is pre-provisioned and ready to connect to your platform.
            </p>
          </div>

          <div className="mb-10">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={HOME_SECTION_IMAGES.hardware.src}
                alt={HOME_SECTION_IMAGES.hardware.alt}
                width={1600}
                height={900}
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1280px) 100vw, 1200px"
                className="h-auto w-full rounded-2xl object-cover"
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gadgets.map((gadget, i) => (
              <motion.div
                key={gadget.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="glass-card rounded-2xl p-6 group"
              >
                <div className="mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-neon-cyan/60">{gadget.category}</span>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{gadget.name}</h3>
                <p className="mb-4 text-sm leading-relaxed text-slate-300">{gadget.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {gadget.specs.map(spec => (
                    <span key={spec} className="rounded-full bg-white/8 px-3 py-1 text-[11px] font-medium text-slate-300 border border-white/10">{spec}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link href="/gadgets">
              <button className="btn-neon-outline rounded-full px-8 py-3 text-sm font-semibold flex items-center gap-2 mx-auto">
                View Full Hardware Catalog
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          WHITE-LABEL SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="defer-render relative py-24 lg:py-32 gradient-hero-vivid">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-4">
                White-Label Platform
              </span>
              <h2 className="text-3xl font-bold text-white sm:text-4xl">
                Launch Your Own <span className="text-gradient">Telematics Brand</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-300">
                Deploy a fully white-labeled telematics platform under your brand. Custom domains, logos, CMS landing pages,
                branded mobile apps, and isolated billing. Zero development required &mdash; go to market in weeks, not years.
              </p>
              <div className="mt-8 space-y-4">
                {whitelabelBenefits.map(b => (
                  <div key={b.title} className="flex gap-4">
                    <div className="flex-shrink-0 mt-1 rounded-lg bg-neon-purple/10 p-2 text-neon-purple">
                      <b.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white">{b.title}</h4>
                      <p className="text-sm text-slate-300">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link href="/white-label">
                  <button className="btn-neon rounded-full px-8 py-3 text-sm font-semibold flex items-center gap-2">
                    Explore White-Label
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
              </div>
            </div>

            {/* Mock branded dashboard */}
            <div className="relative space-y-6">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={HOME_SECTION_IMAGES.whiteLabel.src}
                  alt={HOME_SECTION_IMAGES.whiteLabel.alt}
                  width={1600}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="h-auto w-full rounded-2xl object-cover"
                />
              </div>
              <div className="glass-card-heavy rounded-2xl p-1">
                <div className="rounded-xl bg-gradient-to-br from-[#1a1952] to-[#0f0e2e] p-6 space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-neon-purple to-neon-cyan" />
                    <div>
                      <div className="h-3 w-24 rounded bg-white/10" />
                      <div className="mt-1 h-2 w-16 rounded bg-white/8" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="rounded-xl bg-white/8 p-4 space-y-2">
                        <div className="h-2 w-8 rounded bg-neon-cyan/20" />
                        <div className="h-5 w-12 rounded bg-white/10" />
                      </div>
                    ))}
                  </div>
                  <div className="h-32 rounded-xl bg-white/6 border border-white/10 flex items-center justify-center">
                    <Globe className="h-8 w-8 text-white/10" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-20 rounded-xl bg-white/8" />
                    <div className="h-20 rounded-xl bg-white/8" />
                  </div>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 rounded-full glass-card px-3 py-1.5 text-[10px] font-semibold text-neon-cyan">
                Your Brand Here
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
          ══════════════════════════════════════════════════════ */}
      <section className="defer-render relative py-24 lg:py-32 gradient-hero-vivid">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan mb-4">
              Getting Started
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Go Live in <span className="text-gradient">Three Steps</span>
            </h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {[
              { number: '01', title: 'Sign Up & Configure', desc: 'Create your organization, choose a plan, upload your logo, and configure services in our guided wizard.', icon: Users },
              { number: '02', title: 'Connect Devices', desc: 'Install GPS trackers and cameras on your vehicles. Our SMS provisioning and AI-guided setup make it effortless.', icon: Zap },
              { number: '03', title: 'Track & Optimize', desc: 'Monitor your fleet live, receive AI-powered insights, generate reports, and continuously improve operations.', icon: Sparkles },
            ].map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                {i < 2 && (
                  <div className="hidden lg:block absolute top-12 left-[calc(50%+40px)] right-[calc(-50%+40px)] h-px">
                    <div className="h-full w-full border-t border-dashed border-neon-cyan/20" />
                  </div>
                )}
                <div className="glass-card rounded-2xl p-8 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-neon-cyan/5 text-neon-cyan border border-neon-cyan/10">
                    <step.icon className="h-7 w-7" />
                  </div>
                  <span className="mb-2 inline-block text-xs font-bold uppercase tracking-wider text-neon-cyan/60">Step {step.number}</span>
                  <h3 className="mb-2 text-lg font-semibold text-white">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-300">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          RECENT BLOGS
          ══════════════════════════════════════════════════════ */}
      <section className="defer-render relative py-24 lg:py-28">
        <div className="absolute inset-0 gradient-hero-vivid opacity-70" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan">
              <Sparkles className="h-3.5 w-3.5" />
              Latest From Our Blog
            </span>
            <h2 className="mt-4 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Fresh Insights in <span className="text-gradient">8 Highlights</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              Deep dives on telematics, safety, and fleet optimization from the GlonexTrack team.
            </p>
          </div>

          {recentBlogsLoading ? (
            <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-[320px] animate-pulse rounded-3xl border border-white/10 bg-white/5" />
              ))}
            </div>
          ) : recentBlogs.length > 0 ? (
            <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {recentBlogs.map((article) => (
                <article key={article.id} className="group [perspective:1100px]">
                  <div className="relative h-[320px] w-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                    <div className="absolute inset-0 overflow-hidden rounded-3xl border border-white/10 bg-slate-950/75 [backface-visibility:hidden]">
                      <Link href={`/blog/${encodeURIComponent(article.slug)}`} className="block h-full">
                        <div className="relative h-40 w-full overflow-hidden">
                          {article.featuredImage?.url ? (
                            <Image
                              src={article.featuredImage.url}
                              alt={article.featuredImage.alt || article.title}
                              fill
                              loading="lazy"
                              decoding="async"
                              sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 25vw"
                              className="object-cover transition duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center bg-gradient-to-br from-cyan-500/20 via-violet-500/20 to-fuchsia-500/20 text-sm text-slate-100">
                              {article.title}
                            </div>
                          )}
                        </div>
                        <div className="flex h-[calc(100%-10rem)] flex-col p-4">
                          <div className="text-[11px] uppercase tracking-[0.14em] text-cyan-200">
                            {article.categoryName || 'General'}
                          </div>
                          <h3 className="mt-2 line-clamp-2 text-base font-semibold text-white">
                            {article.title}
                          </h3>
                          <p className="mt-2 flex-1 text-xs leading-relaxed text-slate-300">
                            {to35Words(article.preview || article.excerpt || '')}
                          </p>
                          <span className="mt-3 text-xs font-semibold text-cyan-200">
                            Read more →
                          </span>
                        </div>
                      </Link>
                    </div>

                    <div className="absolute inset-0 rounded-3xl border border-cyan-400/30 bg-slate-950/90 p-6 [backface-visibility:hidden] [transform:rotateY(180deg)]">
                      <Link
                        href={`/blog/${encodeURIComponent(article.slug)}`}
                        className="flex h-full flex-col justify-between"
                      >
                        <div>
                          <div className="inline-flex rounded-full border border-cyan-300/40 bg-cyan-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-cyan-200">
                            Reading Time
                          </div>
                          <p className="mt-4 text-4xl font-bold text-white">
                            {article.readingMinutes} min
                          </p>
                          <p className="mt-2 text-sm text-slate-300">
                            Published {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : 'recently'}
                          </p>
                        </div>
                        <span className="rounded-full border border-white/20 px-4 py-2 text-center text-sm font-semibold text-white hover:border-cyan-300/50 hover:text-cyan-200">
                          Open article
                        </span>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10 bg-slate-900/60 p-8 text-center text-slate-300">
              Blog highlights will appear here as soon as articles are published.
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-2.5 text-sm font-semibold text-white hover:border-cyan-300/50 hover:text-cyan-200"
            >
              Explore all articles
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA BANNER
          ══════════════════════════════════════════════════════ */}
      <section className="defer-render relative overflow-hidden py-24 lg:py-32">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,212,255,0.12) 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 30% 50%, rgba(168,85,247,0.1) 0%, transparent 50%), radial-gradient(ellipse 50% 50% at 70% 70%, rgba(244,63,94,0.06) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0 grid-bg" />

        {/* Animated colorful particles */}
        {[
          { cls: 'dot-pulse-cyan', top: '15%', left: '10%' },
          { cls: 'dot-pulse-purple', top: '20%', right: '15%' },
          { cls: 'dot-pulse-magenta', bottom: '25%', left: '20%' },
          { cls: 'dot-pulse-cyan', top: '60%', right: '8%' },
          { cls: 'dot-pulse-purple', bottom: '15%', right: '30%' },
          { cls: 'dot-pulse-magenta', top: '35%', left: '5%' },
          { cls: 'dot-pulse-cyan', bottom: '10%', left: '45%' },
          { cls: 'dot-pulse-purple', top: '10%', left: '60%' },
        ].map((dot, i) => (
          <motion.div
            key={i}
            className={`absolute ${dot.cls}`}
            style={{ top: dot.top, left: dot.left, right: dot.right, bottom: dot.bottom }}
            animate={{ y: [0, -10, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.3 }}
          />
        ))}

        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center lg:px-8">
          <div className="mx-auto mb-8 max-w-3xl">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={HOME_SECTION_IMAGES.cta.src}
                alt={HOME_SECTION_IMAGES.cta.alt}
                width={1600}
                height={900}
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1280px) 100vw, 1200px"
                className="h-auto w-full rounded-2xl object-cover"
              />
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
          >
            Ready to Transform Your <span className="text-gradient">Fleet Operations?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mx-auto mt-4 max-w-2xl text-lg text-slate-300"
          >
            Join 300+ organizations that trust GlonexTrack for real-time tracking, camera intelligence,
            driver safety, and fleet optimization across 42 countries.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-8 flex flex-col justify-center gap-4 sm:flex-row"
          >
            <Link href="/signup">
              <button className="btn-neon rounded-full px-10 py-4 text-sm font-semibold flex items-center gap-2 mx-auto sm:mx-0">
                Start Free Trial
                <ChevronRight className="h-4 w-4" />
              </button>
            </Link>
            <Link href="/contact">
              <button className="btn-neon-outline rounded-full px-10 py-4 text-sm font-semibold flex items-center gap-2 mx-auto sm:mx-0">
                Book a Demo
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
